import type { InterpolationCtxType } from '@/contexts/InterpolationContext';
import Mustache from 'mustache';

interface InterpolateOptions {
    preserveUndefinedTemplates?: boolean;
}

// Helper to check if a value exists in nested object
function getNestedValue(obj: any, path: string): any {
    return path.split('.').reduce((curr, key) => curr?.[key], obj);
}

function decodeHtmlEntities(value: string): string {
    if (typeof document === 'undefined') {
        return value;
    }
    const textarea = document.createElement('textarea');
    textarea.innerHTML = value;
    return textarea.value;
}

export function deepInterpolate<T>(obj: T, ctx: InterpolationCtxType, options?: InterpolateOptions): T {
    if (obj === null || obj === undefined) return obj;
    if (typeof obj === 'string') {
        try {
            // If preserveUndefinedTemplates is true, check if all variables exist
            if (options?.preserveUndefinedTemplates) {
                const tokens = Mustache.parse(obj);
                let hasUndefinedVariables = false;

                // Check each mustache variable token
                for (const token of tokens) {
                    // token[0] is the type, token[1] is the variable name
                    if (token[0] === 'name' && getNestedValue(ctx, token[1]) === undefined) {
                        hasUndefinedVariables = true;
                        break;
                    }
                }

                // If any variable is undefined, return the original template string
                if (hasUndefinedVariables) {
                    return obj;
                }
            }

            return decodeHtmlEntities(Mustache.render(obj as string, ctx)) as unknown as T;
        } catch {
            return obj;
        }
    }
    if (Array.isArray(obj)) {
        return obj.map((item) => deepInterpolate(item, ctx, options)) as unknown as T;
    }
    if (typeof obj === 'object') {
        const out: Record<string, unknown> = {};
        for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
            out[k] = deepInterpolate(v, ctx, options);
        }
        return out as unknown as T;
    }
    return obj;
}
